import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

console.log('🌐 API Base URL:', API_BASE_URL);

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if exists
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log('🔵 API Request:', config.method?.toUpperCase(), config.url, 'Timeout:', config.timeout);
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - with smart retry logic
const requestRetryMap = new Map<string, number>();
const MAX_RETRIES = 2;

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Clear retry count on success
    const requestKey = `${response.config.method}:${response.config.url}`;
    requestRetryMap.delete(requestKey);
    
    console.log('✅ API Response:', response.config.url, response.status);
    console.log('📦 Response Data:', response.data);
    
    // Return the full response data (don't unwrap here)
    return response;
  },
  async (error: AxiosError) => {
    const requestKey = `${error.config?.method}:${error.config?.url}`;
    const retryCount = requestRetryMap.get(requestKey) || 0;
    
    console.error('❌ API Error:', error.response?.data || error.message);
    console.error('📊 Error Status:', error.response?.status);
    console.error('⏱️ Error Code:', error.code);
    
    // Don't retry rate limiting (429), client errors (4xx), or if max retries reached
    if (error.response?.status === 429) {
      console.error('⚠️ Rate Limited (429) - Waiting before next request');
      return Promise.reject(new Error('Too many requests. Please wait a moment.'));
    }
    
    // Only retry on network errors and 5xx server errors
    const shouldRetry = retryCount < MAX_RETRIES && (
      error.code === 'ECONNABORTED' || 
      error.code === 'ECONNREFUSED' || 
      (error.response?.status && error.response.status >= 500)
    );
    
    if (shouldRetry) {
      requestRetryMap.set(requestKey, retryCount + 1);
      const delay = 1000 * (retryCount + 1); // Exponential backoff: 1s, 2s
      console.warn(`🔄 Retrying request (${retryCount + 1}/${MAX_RETRIES}) after ${delay}ms...`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return apiClient.request(error.config as any);
    }
    
    // Clean up retry map
    requestRetryMap.delete(requestKey);
    
    const message = (error.response?.data as any)?.message || error.message || 'Something went wrong';
    return Promise.reject(new Error(message));
  }
);

export default apiClient;